import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin } from "lucide-react";

export default function LocalPage() {
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone_eg: "", role: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/local");
            setForm({
                first_name: data.first_name || "",
                last_name: data.last_name || "",
                email: data.email || "",
                phone_eg: data.phone_eg || "",
                role: data.role || "",
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/local", form);
            toast.success("Lokaler Kontakt gespeichert.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <AppLayout><div className="text-sm text-ink-tertiary">Laden …</div></AppLayout>;
    }

    return (
        <AppLayout>
            <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.18em] text-ink-tertiary mb-3">Ägypten</p>
                <h1 className="text-4xl font-bold mb-2 text-ink" style={{ fontFamily: "Space Grotesk" }}>
                    Lokaler <span className="italic font-normal" style={{ fontFamily: "Fraunces" }}>Kontakt</span>
                </h1>
                <p className="text-sm text-ink-secondary mb-8">
                    Dein Ansprechpartner vor Ort. Diese Daten erscheinen automatisch in der Fußzeile jedes PDFs.
                </p>

                <form onSubmit={submit} className="pt-card p-8 space-y-5" data-testid="local-form">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-terracotta text-white flex items-center justify-center">
                            <MapPin className="w-6 h-6" strokeWidth={1.5} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-ink">{`${form.first_name} ${form.last_name}`.trim() || "—"}</p>
                            <p className="text-xs text-ink-tertiary">{form.role || "Lokaler Ansprechpartner"}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Vorname" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="local-first-name" />
                        <Field label="Nachname" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="local-last-name" />
                    </div>
                    <Field label="Rolle (optional)" value={form.role} onChange={(v) => setForm({ ...form, role: v })} placeholder="z.B. Local Guide, Reiseleiter" testid="local-role" />
                    <Field label="E-Mail" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="local-email" />
                    <Field label="Telefon EG" type="tel" value={form.phone_eg} onChange={(v) => setForm({ ...form, phone_eg: v })} placeholder="+20 100 ..." testid="local-phone" />

                    <div className="flex justify-end pt-4 border-t border-sand-300">
                        <button type="submit" disabled={saving} data-testid="local-save"
                            className="bg-navy hover:bg-navy-hover text-sand rounded-md px-6 py-2.5 font-medium shadow-card disabled:opacity-50">
                            {saving ? "Wird gespeichert ..." : "Speichern"}
                        </button>
                    </div>
                </form>
            </div>
        </AppLayout>
    );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-navy"
            />
        </div>
    );
}
